// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useAuth } from './useAuth';
import { usePersona } from './usePersona';
import { useShellStore } from '../stores/shellStore';
import { STATIC_SITES } from '../config/sites';
import { OPERATOR_SITE_ID } from '../mocks/alarms';

/** Sentinel selectedSiteId meaning "the whole fleet" (managers/admins only). */
export const FLEET_ID = '__fleet__';

export interface SiteOption {
  siteId: string;
  label: string;
}

export interface ActiveSite {
  /** The site the Site page scopes to (always a real site id, for links). */
  siteId: string;
  /** Header label — the site name, or "Whole fleet" in fleet mode. */
  siteLabel: string;
  /** Real sites the user may view (from OMS / the /me response). */
  options: SiteOption[];
  /** True when the persona may switch sites (Ops Manager / Admin). */
  canSwitch: boolean;
  /** True when the manager has chosen the whole fleet rather than one site. */
  isFleet: boolean;
  /** Select a specific site (no-op for operators). */
  setSite: (_siteId: string) => void;
  /** Switch to the whole-fleet view (managers/admins). */
  setFleet: () => void;
}

/**
 * Resolves the active site + whether the persona may switch it.
 *
 * - **Operator** is locked to their single assigned site (no dropdown, never
 *   fleet). Site comes from OMS / the /me response.
 * - **Ops Manager / Admin** (persona `manager`) may switch across every site
 *   they can access, or pick the **whole fleet** (the default). A specific
 *   site drives the Site page; whole fleet swaps the Site rail item for Fleet.
 * - Executives read a portfolio view and do not switch a Site page here.
 */
export function useActiveSite(): ActiveSite {
  const { sites } = useAuth();
  const { persona } = usePersona();
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const setSelectedSite = useShellStore(s => s.setSelectedSite);

  const options: SiteOption[] =
    sites.length > 0
      ? sites.map(s => ({ siteId: s.site_id, label: s.label }))
      : STATIC_SITES.map(s => ({ siteId: s.id, label: s.label }));

  // The operator's locked site: prefer GA-10, else the first assigned site.
  const operatorOption = options.find(o => o.siteId === OPERATOR_SITE_ID) ?? options[0];

  const setSite = (siteId: string) => {
    const opt = options.find(o => o.siteId === siteId);
    setSelectedSite(siteId, opt?.label ?? siteId);
  };
  const setFleet = () => setSelectedSite(FLEET_ID, 'Whole fleet');

  if (persona === 'operator') {
    return {
      siteId: operatorOption?.siteId ?? OPERATOR_SITE_ID,
      siteLabel: operatorOption?.label ?? OPERATOR_SITE_ID,
      options,
      canSwitch: false,
      isFleet: false,
      setSite: () => {},
      setFleet: () => {},
    };
  }

  // Manager / admin: whole fleet is the default (null / sentinel selection).
  const isFleet = persona === 'manager' && (selectedSiteId == null || selectedSiteId === FLEET_ID);
  const activeReal = options.find(o => o.siteId === selectedSiteId) ?? operatorOption;

  return {
    siteId: activeReal?.siteId ?? OPERATOR_SITE_ID,
    siteLabel: isFleet ? 'Whole fleet' : (activeReal?.label ?? OPERATOR_SITE_ID),
    options,
    canSwitch: persona === 'manager' && options.length > 0,
    isFleet,
    setSite,
    setFleet,
  };
}
